/**
 * OPUS ci · idea 3 — Números generadores.
 *
 * El documento que demuestra de dónde sale la cantidad de cada renglón de la hoja, como se entrega en obra
 * en México: una hoja por concepto con encabezado (obra, plano, fecha, clave, concepto, unidad), croquis de
 * localización, la tabla Localización | Piezas | Largo | Ancho | Alto | Parcial agrupada por nivel o planta
 * con subtotales, el total que cuadra con la hoja y, al pie, «Elaboró» / «Revisó».
 *
 * Lo que se muestra es la FOTO que se guardó al agregar el renglón (`renglon.generador`); el croquis se toma
 * del visor del plano al verlo o imprimirlo, uno por concepto, con sus piezas resaltadas y rotuladas con el
 * número de su línea. Nada de ejes ni fachadas: la localización es el nombre de la pieza en el modelo.
 *
 *  - `DocumentoGenerador`: el documento de un renglón (pantalla e impresión).
 *  - `VistaGeneradores`: todos los conceptos a un lado, el generador del elegido y su croquis grande.
 *  - `ImpresionGeneradores`: todos los conceptos, una hoja cada uno, para imprimir (carta horizontal).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spinner } from '@fluentui/react-components';
import { Checkmark16Filled, Print20Regular, Warning16Filled } from '@fluentui/react-icons';
import {
  armarDocumentoGenerador,
  claveDeElemento,
  SIN_FOTO_GENERADOR,
  type LineaGenerador,
  type PlanoRenglon,
} from '@supernova/shared';
import type { Croquis } from '../../lib/croquis.js';

/** El croquis de un renglón (todo el concepto, `null`) o de una de sus líneas, tomado del visor. */
export type CapturarCroquis = (renglon: PlanoRenglon, linea: LineaGenerador | null) => Promise<Croquis | null>;

const fmt = (n: number, dec: number) => n.toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtMedida = (n: number | null) => (n == null ? '' : fmt(n, 2));
const unidadBonita = (u: string | null | undefined) => (u ?? '').replace(/m2\b/i, 'm²').replace(/m3\b/i, 'm³');
const fechaHoy = () => new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });

/** La clave con que una línea se rotula en el croquis: su pieza (IFC) o su elemento (DWG). */
export function claveDeMarca(l: LineaGenerador): string | null {
  if (l.pieza) return `p:${l.pieza}`;
  return l.elemento ? `e:${claveDeElemento(l.elemento)}` : null;
}

/** Las líneas que se numeran (las que miden algo; los descuentos van debajo de la suya) y su número. */
export function numerosDeLineas(lineas: readonly LineaGenerador[]): Map<LineaGenerador, number> {
  const out = new Map<LineaGenerador, number>();
  let n = 0;
  for (const l of lineas) if (l.clase !== 'descuento' && !(l.clase === 'medida' && l.piezas == null && l.detalle?.startsWith('El modelo mide'))) out.set(l, ++n);
  return out;
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '5px 8px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-subtle)',
  background: 'var(--bg-raised)',
};
const td: React.CSSProperties = { padding: '5px 8px', fontSize: 12.5, borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/** El croquis con el número de cada línea sobre su pieza. */
export function ImagenCroquis({
  croquis,
  numeros,
  cargando,
  sinCroquis,
  alto,
}: {
  croquis: Croquis | null;
  /** Clave de marca → números de línea que la rotulan. */
  numeros: Map<string, number[]>;
  cargando?: boolean;
  sinCroquis?: string | null;
  alto?: number;
}) {
  if (!croquis) {
    return (
      <div
        style={{
          height: alto ?? 220,
          background: 'var(--bg-raised)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--text-tertiary)',
        }}
      >
        {cargando ? <Spinner size="tiny" label="Tomando el croquis…" /> : sinCroquis ?? 'Sin croquis.'}
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', width: '100%', lineHeight: 0 }}>
      <img src={croquis.url} alt="Croquis de localización" style={{ width: '100%', borderRadius: 4, border: '1px solid var(--border-subtle)', background: '#fff' }} />
      {croquis.marcas.map((m) => {
        const ns = numeros.get(m.k);
        if (!ns?.length) return null;
        return (
          <span
            key={m.k}
            style={{
              position: 'absolute',
              left: `${m.x * 100}%`,
              top: `${m.y * 100}%`,
              transform: 'translate(-50%, -50%)',
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 9,
              background: '#fff',
              border: '1.5px solid #0c6fd6',
              color: '#0c6fd6',
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: '15px',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {ns.join(',')}
          </span>
        );
      })}
    </div>
  );
}

/** La hoja de un concepto. `onLinea` = tocar una línea (para resaltar su pieza en el croquis). */
export function DocumentoGenerador({
  renglon,
  obra,
  plano,
  croquis,
  cargandoCroquis,
  sinCroquis,
  onLinea,
  lineaFoco,
  conCroquis = true,
  firmas = false,
}: {
  renglon: PlanoRenglon;
  obra?: string | null;
  plano?: string | null;
  croquis: Croquis | null;
  cargandoCroquis?: boolean;
  /** Por qué no hay croquis (otro plano, lo que no trae el plano). */
  sinCroquis?: string | null;
  onLinea?: (l: LineaGenerador | null) => void;
  lineaFoco?: LineaGenerador | null;
  /** false = el croquis lo muestra quien llama (la vista lo pone grande a un lado). */
  conCroquis?: boolean;
  /** Las dos líneas de firma al pie (la hoja impresa). */
  firmas?: boolean;
}) {
  const d = armarDocumentoGenerador(renglon);
  const foto = d.foto;
  const u = unidadBonita(renglon.unidad);
  const cols = d.columnas;
  const nCols = 3 + Number(cols.piezas) + Number(cols.largo) + Number(cols.ancho) + Number(cols.alto);
  const dec = d.decimales;
  const numeros = useMemo(() => numerosDeLineas(foto?.lineas ?? []), [foto]);
  const porMarca = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const [l, n] of numeros) {
      const k = claveDeMarca(l);
      if (k) (m.get(k) ?? m.set(k, []).get(k)!).push(n);
    }
    return m;
  }, [numeros]);
  const dato = (etiqueta: string, valor: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 6, fontSize: 12.5, lineHeight: 1.4 }}>
      <span style={{ color: 'var(--text-tertiary)', minWidth: 62 }}>{etiqueta}</span>
      <span style={{ flex: 1 }}>{valor}</span>
    </div>
  );
  return (
    <div className="generador-seccion" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Número generador</div>
          {dato('Obra', obra || '—')}
          {dato('Plano', plano || '—')}
          {dato('Fecha', fechaHoy())}
          {dato('Clave', renglon.resourceClave || '—')}
          {dato('Concepto', renglon.conceptoDescripcion)}
          {dato(
            'Unidad',
            <>
              <b>{u || '—'}</b> · cantidad en la hoja: <b>{fmt(renglon.cantidad, Math.max(dec, decimalesDe(renglon.cantidad)))} {u}</b>
            </>,
          )}
          {foto?.origen === 'complementario' && (
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.45 }}>
              Es un adicional: su cantidad sale de {foto.lineas.length === 1 ? 'su concepto principal' : `${foto.lineas.length} conceptos principales`} con
              esta regla{foto.regla ? `: ${foto.regla.replace(/\.$/, '')}.` : '.'}
            </div>
          )}
          {foto?.altoUsado && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              Alto de muro usado: {fmt(foto.altoUsado.valorM, 2)} m{foto.altoUsado.deDonde ? ` (${foto.altoUsado.deDonde.replace(/\.$/, '')})` : ''}.
            </div>
          )}
          {foto?.espesorUsadoM && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Espesor usado: {fmt(foto.espesorUsadoM, 2)} m.</div>
          )}
        </div>
        {conCroquis && (
          <div className="generador-croquis" style={{ flex: '0 0 360px', maxWidth: '100%' }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 4, lineHeight: 1.3 }}>
              Croquis de localización {lineaFoco ? `· ${lineaFoco.localizacion}` : ''}
            </div>
            <ImagenCroquis croquis={croquis} numeros={porMarca} cargando={cargandoCroquis} sinCroquis={sinCroquis} />
          </div>
        )}
      </div>

      {d.estado === 'sin_foto' && (
        <div style={{ fontSize: 13, padding: '10px 12px', borderRadius: 6, background: 'var(--bg-raised)' }}>{SIN_FOTO_GENERADOR}</div>
      )}
      {d.estado === 'a_mano' && (
        <div style={{ fontSize: 13, padding: '10px 12px', borderRadius: 6, background: 'var(--bg-raised)' }}>
          El plano no da esta cantidad con la unidad de este concepto: se escribe a mano en la hoja (hoy dice{' '}
          {fmt(renglon.cantidad, decimalesDe(renglon.cantidad))} {u}). No hay medición que mostrar.
        </div>
      )}

      {d.estado === 'ok' && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 28 }}>#</th>
              <th style={th}>Localización</th>
              {cols.piezas && <th style={{ ...th, textAlign: 'right' }}>Piezas</th>}
              {cols.largo && <th style={{ ...th, textAlign: 'right' }}>Largo</th>}
              {cols.ancho && <th style={{ ...th, textAlign: 'right' }}>Ancho</th>}
              {cols.alto && <th style={{ ...th, textAlign: 'right' }}>Alto</th>}
              <th style={{ ...th, textAlign: 'right' }}>Parcial</th>
            </tr>
          </thead>
          {d.grupos.map((g, gi) => (
            <tbody key={`${g.nombre}#${gi}`}>
              {(d.grupos.length > 1 || g.nombre) && (
                <tr>
                  <td colSpan={nCols - 1} style={{ ...td, fontWeight: 700, background: 'var(--bg-raised)' }}>
                    {g.nombre || 'Sin nivel ni planta'}
                  </td>
                  <td style={{ ...num, fontWeight: 700, background: 'var(--bg-raised)' }}>{fmt(g.subtotal, dec)}</td>
                </tr>
              )}
              {g.lineas.map((l, i) => {
                const foco = lineaFoco === l;
                const n = numeros.get(l);
                const resta = l.parcial < 0;
                return (
                  <tr
                    key={i}
                    onClick={onLinea ? () => onLinea(foco ? null : l) : undefined}
                    style={{ cursor: onLinea ? 'pointer' : undefined, background: foco ? 'var(--accent-weak)' : undefined }}
                  >
                    <td style={{ ...td, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{n ?? ''}</td>
                    <td style={{ ...td, paddingLeft: n ? 8 : 22 }}>
                      <span style={{ color: resta ? 'var(--bad, #c4314b)' : undefined, fontWeight: n ? 600 : 400 }}>{l.localizacion}</span>
                      {(l.detalle || l.estimado) && (
                        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.35 }}>
                          {/* Un adicional de dos tipos con el mismo nombre legible: se dice de qué tipo del plano sale. */}
                          {l.clase === 'regla' && l.elemento && l.elemento.clave !== l.localizacion ? `«${l.elemento.clave}» · ` : ''}
                          {l.detalle}
                          {l.estimado && <span style={{ color: 'var(--warn, #b7791f)' }}>{l.detalle ? ' · ' : ''}medido del dibujo (el modelo no lo declara)</span>}
                        </div>
                      )}
                    </td>
                    {cols.piezas && <td style={num}>{l.piezas != null ? fmt(l.piezas, Number.isInteger(l.piezas) ? 0 : 2) : ''}</td>}
                    {cols.largo && <td style={num}>{fmtMedida(l.largo)}</td>}
                    {cols.ancho && <td style={num}>{fmtMedida(l.ancho)}</td>}
                    {cols.alto && <td style={num}>{fmtMedida(l.alto)}</td>}
                    <td style={{ ...num, fontWeight: n ? 600 : 400, color: resta ? 'var(--bad, #c4314b)' : undefined }}>{fmt(l.parcial, dec)}</td>
                  </tr>
                );
              })}
            </tbody>
          ))}
          <tfoot>
            {d.ajusteRedondeo !== 0 && (
              <tr>
                <td colSpan={nCols - 1} style={{ ...td, color: 'var(--text-secondary)' }}>
                  Redondeo de los parciales a {dec} decimales
                </td>
                <td style={num}>{fmt(d.ajusteRedondeo, dec)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={nCols - 1} style={{ ...td, fontWeight: 700 }}>
                Total medido del plano{' '}
                {d.cuadra && !d.corregidoAMano && (
                  <span style={{ color: 'var(--ok, #107c10)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <Checkmark16Filled style={{ verticalAlign: '-3px' }} /> cuadra con tu hoja de presupuesto
                  </span>
                )}
                {!d.cuadra && (
                  <span style={{ color: 'var(--bad, #c4314b)', fontWeight: 600 }}>
                    <Warning16Filled style={{ verticalAlign: '-3px' }} /> no cuadra con tu hoja ({fmt(renglon.cantidadMedida ?? renglon.cantidad, dec)} {u})
                  </span>
                )}
              </td>
              <td style={{ ...num, fontWeight: 700 }}>
                {fmt(d.medido ?? 0, dec)} {u}
              </td>
            </tr>
            {d.corregidoAMano && (
              <tr>
                <td colSpan={nCols} style={{ ...td, color: 'var(--warn, #b7791f)', fontWeight: 600 }}>
                  Medido del plano: {fmt(d.corregidoAMano.medido, dec)} {u} · corregido a mano a {fmt(d.corregidoAMano.aMano, Math.max(dec, decimalesDe(d.corregidoAMano.aMano)))} {u}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      )}

      {foto?.origen === 'no_dibujado' && foto.supuesto && (
        <div style={{ fontSize: 12.5, lineHeight: 1.5, padding: '8px 12px', borderRadius: 6, background: 'var(--bg-raised)' }}>
          <b>No sale de piezas dibujadas.</b> El plano no trae este trabajo: la cantidad es un supuesto
          {foto.supuesto.deDonde ? ` (${foto.supuesto.deDonde.replace(/\.$/, '')})` : ''}.
          {foto.supuesto.base.length > 0 && (
            <>
              {' '}Se estimó con lo que el plano sí trae:
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {foto.supuesto.base.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {firmas && (
        <div className="generador-firmas" style={{ display: 'flex', gap: 60, marginTop: 36, padding: '0 40px' }}>
          {['Elaboró', 'Revisó'].map((x) => (
            <div key={x} style={{ flex: 1, textAlign: 'center', fontSize: 12 }}>
              <div style={{ borderTop: '1px solid var(--text-primary)', paddingTop: 4 }}>{x}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function decimalesDe(n: number): number {
  for (let d = 0; d <= 3; d++) if (Math.abs(Math.round(n * 10 ** d) / 10 ** d - n) < 1e-9) return d;
  return 3;
}

/** Por qué un renglón no tiene croquis (o null si sí se puede tomar). */
export function motivoSinCroquis(r: PlanoRenglon, fileIdAbierto: string | null | undefined): string | null {
  if (r.generador?.origen === 'no_dibujado' || (!r.seleccion && !r.generador?.lineas.some((l) => l.elemento))) {
    return 'No sale de piezas dibujadas: no hay croquis.';
  }
  if (r.sourceFileId && fileIdAbierto && r.sourceFileId !== fileIdAbierto) return 'Abre su plano para ver el croquis.';
  return null;
}

/** Marcas que pide el croquis de un renglón: las líneas numeradas. */
function marcasDe(r: PlanoRenglon): Map<string, number[]> {
  const m = new Map<string, number[]>();
  for (const [l, n] of numerosDeLineas(r.generador?.lineas ?? [])) {
    const k = claveDeMarca(l);
    if (k) (m.get(k) ?? m.set(k, []).get(k)!).push(n);
  }
  return m;
}

/**
 * «Números generadores» del plano (o de toda la obra): todos los conceptos de la hoja a un lado, el
 * generador del elegido y su croquis grande; tocar una línea resalta su pieza. Desde aquí se imprime.
 */
export function VistaGeneradores({
  renglones,
  inicial,
  capturar,
  obra,
  planoDe,
  fileIdAbierto,
  onCerrar,
}: {
  renglones: PlanoRenglon[];
  inicial: string | null;
  capturar: CapturarCroquis;
  obra?: string | null;
  planoDe: (r: PlanoRenglon) => string | null;
  fileIdAbierto?: string | null;
  onCerrar: () => void;
}) {
  const [elegidoId, setElegidoId] = useState<string | null>(inicial ?? renglones[0]?.id ?? null);
  const [linea, setLinea] = useState<LineaGenerador | null>(null);
  const [croquis, setCroquis] = useState<Croquis | null>(null);
  const [cargando, setCargando] = useState(false);
  // Qué se imprime: todos los conceptos o solo el elegido (su hoja). null = nada.
  const [imprimir, setImprimir] = useState<'todos' | 'este' | null>(null);
  const turno = useRef(0);
  const elegido = renglones.find((r) => r.id === elegidoId) ?? null;
  const sin = elegido ? motivoSinCroquis(elegido, fileIdAbierto) : null;
  useEffect(() => setLinea(null), [elegidoId]);
  useEffect(() => {
    if (!elegido || sin) {
      setCroquis(null);
      return;
    }
    const t = ++turno.current;
    setCargando(true);
    setCroquis(null);
    const h = setTimeout(() => {
      void capturar(elegido, linea)
        .catch(() => null)
        .then((c) => {
          if (turno.current !== t) return;
          setCroquis(c);
          setCargando(false);
        });
    }, 60);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elegidoId, linea]);
  const numerosElegido = useMemo(() => (elegido ? marcasDe(elegido) : new Map<string, number[]>()), [elegido]);
  const numerosFoco = useMemo(() => {
    if (!linea || !elegido) return numerosElegido;
    const k = claveDeMarca(linea);
    return k ? new Map([[k, numerosElegido.get(k) ?? []]]) : new Map<string, number[]>();
  }, [linea, elegido, numerosElegido]);
  const fmtCant = (r: PlanoRenglon) => `${r.cantidad.toLocaleString('es-MX', { maximumFractionDigits: 3 })} ${unidadBonita(r.unidad)}`;
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 999000, background: 'var(--bg-page, #fff)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' }}>
        <b style={{ fontSize: 15 }}>Números generadores</b>
        <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          De dónde sale la cantidad de cada concepto, pieza por pieza, como la pide la dependencia o tu cliente para aceptarla.
        </span>
        <Button icon={<Print20Regular />} disabled={!elegido} onClick={() => setImprimir('este')}>
          Imprimir este
        </Button>
        <Button appearance="primary" icon={<Print20Regular />} disabled={!renglones.length} onClick={() => setImprimir('todos')}>
          Imprimir todos
        </Button>
        <Button onClick={onCerrar}>Cerrar</Button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr) minmax(0, 1.25fr)' }}>
        {/* Todos los conceptos de la hoja */}
        <div style={{ overflowY: 'auto', borderRight: '1px solid var(--border-subtle)' }}>
          {renglones.map((r) => {
            const d = armarDocumentoGenerador(r);
            const sel = r.id === elegidoId;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setElegidoId(r.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  borderBottom: '1px solid var(--border-subtle)',
                  background: sel ? 'var(--accent-weak)' : 'transparent',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                }}
              >
                <div style={{ fontSize: 12.5, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', paddingLeft: r.rol === 'complemento' ? 12 : 0 }}>
                  {r.rol === 'complemento' ? '↳ ' : ''}
                  {r.resourceClave ? <b>{r.resourceClave} </b> : null}
                  {r.conceptoDescripcion}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2, display: 'flex', gap: 6 }}>
                  <span>{fmtCant(r)}</span>
                  {d.estado === 'ok' && d.cuadra && <span style={{ color: 'var(--ok, #107c10)' }}>✓ cuadra</span>}
                  {d.estado === 'ok' && !d.cuadra && <span style={{ color: 'var(--bad, #c4314b)' }}>no cuadra</span>}
                  {d.estado === 'sin_foto' && <span>sin generador</span>}
                  {d.estado === 'a_mano' && <span>escrita a mano</span>}
                </div>
              </button>
            );
          })}
        </div>
        {/* El croquis del elegido, grande */}
        <div style={{ padding: 14, borderRight: '1px solid var(--border-subtle)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Croquis de localización {linea ? `· ${linea.localizacion}` : '· todo el concepto'}
          </div>
          <ImagenCroquis croquis={croquis} numeros={numerosFoco} cargando={cargando} sinCroquis={sin ?? 'No se pudo tomar el croquis.'} alto={420} />
          {!sin && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
              El número sobre cada pieza es el de su línea en la tabla. Toca una línea para ver solo esa pieza; tócala otra vez para ver todo el concepto.
            </div>
          )}
        </div>
        {/* El generador del elegido */}
        <div style={{ padding: 14, overflowY: 'auto' }}>
          {elegido ? (
            <DocumentoGenerador
              renglon={elegido}
              obra={obra}
              plano={planoDe(elegido)}
              croquis={null}
              conCroquis={false}
              onLinea={sin ? undefined : setLinea}
              lineaFoco={linea}
            />
          ) : (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>La hoja todavía no tiene renglones.</div>
          )}
        </div>
      </div>
      {imprimir && (
        <ImpresionGeneradores
          renglones={imprimir === 'este' && elegido ? [elegido] : renglones}
          capturar={capturar}
          obra={obra}
          planoDe={planoDe}
          fileIdAbierto={fileIdAbierto}
          onCerrar={() => setImprimir(null)}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * «Imprimir»: toma el croquis de CADA concepto (uno por uno, del visor abierto: el suyo, con sus piezas
 * resaltadas y numeradas) y arma una hoja por concepto en carta horizontal. Al estar listo abre el diálogo
 * de impresión.
 */
export function ImpresionGeneradores({
  renglones,
  capturar,
  obra,
  planoDe,
  fileIdAbierto,
  onCerrar,
}: {
  renglones: PlanoRenglon[];
  capturar: CapturarCroquis;
  obra?: string | null;
  planoDe: (r: PlanoRenglon) => string | null;
  fileIdAbierto?: string | null;
  onCerrar: () => void;
}) {
  const [croquis, setCroquis] = useState<Map<string, Croquis | null>>(new Map());
  const [listo, setListo] = useState(false);
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const m = new Map<string, Croquis | null>();
      for (const r of renglones) {
        if (!vivo) return;
        m.set(r.id, motivoSinCroquis(r, fileIdAbierto) ? null : await capturar(r, null).catch(() => null));
        setCroquis(new Map(m));
      }
      if (vivo) setListo(true);
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!listo) return;
    const h = setTimeout(() => window.print(), 300);
    return () => clearTimeout(h);
  }, [listo]);
  return createPortal(
    <div id="hoja-generadores" style={{ position: 'fixed', inset: 0, zIndex: 1000000, background: 'var(--bg-page, #fff)', overflow: 'auto' }}>
      <style>{`@media print {
        body > *:not(#hoja-generadores) { display: none !important; }
        #hoja-generadores { position: static !important; overflow: visible !important; background: #fff !important; }
        #hoja-generadores, #hoja-generadores * { color: #000 !important; border-color: #999 !important; }
        #hoja-generadores td, #hoja-generadores th { background: transparent !important; }
        #hoja-generadores .no-imprimir { display: none !important; }
        #hoja-generadores .generador-hoja { break-after: page; border: none !important; padding: 0 !important; }
        #hoja-generadores .generador-hoja:last-child { break-after: auto; }
        #hoja-generadores tr { break-inside: avoid; }
        #hoja-generadores .generador-firmas { break-inside: avoid; }
        @page { size: letter landscape; margin: 10mm; }
      }`}</style>
      <div className="no-imprimir" style={{ position: 'sticky', top: 0, display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)', zIndex: 1 }}>
        <b style={{ flex: 1 }}>Números generadores · {renglones.length === 1 ? "la hoja de este concepto" : "una hoja por concepto"}</b>
        {!listo ? (
          <Spinner size="tiny" label={renglones.length === 1 ? 'Tomando el croquis…' : `Tomando el croquis de cada concepto: ${croquis.size} de ${renglones.length}…`} />
        ) : (
          <Button appearance="primary" icon={<Print20Regular />} onClick={() => window.print()}>
            Imprimir o guardar PDF
          </Button>
        )}
        <Button onClick={onCerrar}>Cerrar</Button>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {renglones.map((r) => (
          <div key={r.id} className="generador-hoja" style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 14 }}>
            <DocumentoGenerador
              renglon={r}
              obra={obra}
              plano={planoDe(r)}
              croquis={croquis.get(r.id) ?? null}
              cargandoCroquis={!croquis.has(r.id)}
              sinCroquis={motivoSinCroquis(r, fileIdAbierto) ?? 'No se pudo tomar el croquis.'}
              firmas
            />
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}
